import React, { useState, Fragment, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookingToast } from './components/BookingToast';
import { Scissors, Star, Phone, MapPin, Clock, MessageSquare, ChevronRight, AlertCircle, Quote, Check, Menu, X, ChevronDown } from 'lucide-react';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/gggR1qXiVdEWKzcyh2TRJt/logo.png';
const BANNER_URL = 'https://cdn.magicpatterns.com/uploads/p2aqsFEaYM15B6jXo8o8CZ/banner.png';
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.7,
  delay,
  ease: 'easeOut'
}} data-id="element-0">
    {children}
  </motion.div>;
const Divider = () => <div className="flex items-center justify-center py-12 opacity-60" data-id="element-1">
    <div className="h-px w-16 bg-gold" data-id="element-2"></div>
    <div className="mx-4 text-gold" data-id="element-3">
      <Scissors size={14} data-id="element-4" />
    </div>
    <div className="h-px w-16 bg-gold" data-id="element-5"></div>
  </div>;
const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  return <header className="fixed top-0 left-0 right-0 z-50 bg-dark/95 backdrop-blur-md border-b border-gold/20" data-id="element-6">
      <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between" data-id="element-7">
        <a href="#" className="flex items-center" data-id="element-8">
          <img src={LOGO_URL} alt="Cristian Barber" className="h-12 object-contain" data-id="element-9" />
        </a>

        <nav className="hidden md:flex items-center gap-8" data-id="element-10">
          <a href="#experience" className="text-sm tracking-widest uppercase text-cream/80 hover:text-gold transition-colors" data-id="element-11">
            Experience
          </a>
          <a href="#why-us" className="text-sm tracking-widest uppercase text-cream/80 hover:text-gold transition-colors" data-id="element-12">
            Why Us
          </a>
          <a href="#testimonials" className="text-sm tracking-widest uppercase text-cream/80 hover:text-gold transition-colors" data-id="element-13">
            Testimonials
          </a>
          <a href="#faq" className="text-sm tracking-widest uppercase text-cream/80 hover:text-gold transition-colors" data-id="element-14">
            FAQ
          </a>
          <a href="#book" className="bg-gold text-dark px-6 py-2.5 font-medium tracking-wide uppercase text-sm hover:bg-gold-light transition-colors" data-id="element-15">
            Book Now
          </a>
        </nav>

        <button className="md:hidden text-gold" onClick={() => setIsOpen(!isOpen)} data-id="element-16">
          {isOpen ? <X data-id="element-17" /> : <Menu data-id="element-18" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isOpen && <div className="md:hidden absolute top-20 left-0 right-0 bg-darker border-b border-gold/20 p-6 flex flex-col gap-6" data-id="element-19">
          <a href="#experience" onClick={() => setIsOpen(false)} className="text-sm tracking-widest uppercase text-cream/80 hover:text-gold" data-id="element-20">
            Experience
          </a>
          <a href="#why-us" onClick={() => setIsOpen(false)} className="text-sm tracking-widest uppercase text-cream/80 hover:text-gold" data-id="element-21">
            Why Us
          </a>
          <a href="#testimonials" onClick={() => setIsOpen(false)} className="text-sm tracking-widest uppercase text-cream/80 hover:text-gold" data-id="element-22">
            Testimonials
          </a>
          <a href="#faq" onClick={() => setIsOpen(false)} className="text-sm tracking-widest uppercase text-cream/80 hover:text-gold" data-id="element-23">
            FAQ
          </a>
          <a href="#book" onClick={() => setIsOpen(false)} className="bg-gold text-dark px-6 py-3 text-center font-medium tracking-wide uppercase text-sm" data-id="element-24">
            Book Now
          </a>
        </div>}
    </header>;
};
const Hero = () => <section className="relative pt-20 bg-dark" data-id="element-25">
    {/* Banner - The Centerpiece */}
    <div className="relative w-full" data-id="element-26">
      <motion.div initial={{
      opacity: 0,
      scale: 1.05
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 1.4,
      ease: 'easeOut'
    }} className="relative w-full aspect-[16/9] md:aspect-[21/9] overflow-hidden" data-id="element-27">
        <img src={BANNER_URL} alt="Cristian Barber - Premium Grooming Studio" className="w-full h-full object-cover" data-id="element-28" />
        {/* Very subtle darkening only at the top edge to blend with header */}
        <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-dark to-transparent pointer-events-none" data-id="element-29"></div>
        {/* Subtle bottom fade into the dark page background */}
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-dark to-transparent pointer-events-none" data-id="element-30"></div>

        {/* Gold corner ornaments framing the banner */}
        <div className="absolute top-6 left-6 w-10 h-10 border-t-2 border-l-2 border-gold/60 pointer-events-none" data-id="element-31"></div>
        <div className="absolute top-6 right-6 w-10 h-10 border-t-2 border-r-2 border-gold/60 pointer-events-none" data-id="element-32"></div>
        <div className="absolute bottom-6 left-6 w-10 h-10 border-b-2 border-l-2 border-gold/60 pointer-events-none" data-id="element-33"></div>
        <div className="absolute bottom-6 right-6 w-10 h-10 border-b-2 border-r-2 border-gold/60 pointer-events-none" data-id="element-34"></div>
      </motion.div>
    </div>

    {/* Editorial content beneath the banner */}
    <div className="relative max-w-5xl mx-auto px-6 pt-16 pb-24 text-center" data-id="element-35">
      <motion.div initial={{
      opacity: 0,
      y: 30
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 1,
      delay: 0.4
    }} data-id="element-36">
        <div className="flex items-center justify-center gap-3 mb-8 text-gold" data-id="element-37">
          <div className="h-px w-12 bg-gold/60" data-id="element-38"></div>
          <span className="text-xs tracking-[0.3em] uppercase" data-id="element-39">
            Lawrenceville, Georgia
          </span>
          <div className="h-px w-12 bg-gold/60" data-id="element-40"></div>
        </div>

        <h1 className="text-5xl md:text-7xl lg:text-8xl font-serif text-gold mb-8 leading-[1.05]" data-id="element-41">
          The Last Barber
          <br data-id="element-42" />
          <span className="italic text-cream/95" data-id="element-43">You'll Ever Need To Try</span>
        </h1>
        <p className="text-xl md:text-2xl text-cream/80 font-light mb-4 max-w-2xl mx-auto leading-relaxed" data-id="element-44">
          Meet Cristian — the barber crafting the image of men who refuse to
          settle.
        </p>
        <p className="text-base md:text-lg text-cream/60 font-light italic mb-12 max-w-2xl mx-auto leading-relaxed" data-id="element-45">
          Your Next Haircut Should Make You Feel Like a Completely Different Man
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6" data-id="element-46">
          <a href="tel:+14704036260" className="w-full sm:w-auto flex items-center justify-center gap-3 bg-gold text-dark px-8 py-4 font-medium tracking-widest uppercase hover:bg-gold-light transition-colors" data-id="element-47">
            <Phone size={18} data-id="element-48" />
            Call To Book
          </a>
          <a href="sms:+14704036260" className="w-full sm:w-auto flex items-center justify-center gap-3 border border-gold text-gold px-8 py-4 font-medium tracking-widest uppercase hover:bg-gold/10 transition-colors" data-id="element-49">
            <MessageSquare size={18} data-id="element-50" />
            Text To Book
          </a>
        </div>

        <div className="mt-12 flex items-center justify-center gap-2 text-cream/60 text-sm tracking-widest uppercase" data-id="element-51">
          <MapPin size={14} className="text-gold" data-id="element-52" />
          2070 Sugarloaf Pkwy #300, Lawrenceville, GA
        </div>
      </motion.div>
    </div>
  </section>;
const Hook = () => <section id="experience" className="py-24 bg-dark" data-id="element-53">
    <div className="max-w-3xl mx-auto px-6" data-id="element-54">
      <FadeIn data-id="element-55">
        <div className="prose prose-invert prose-lg mx-auto" data-id="element-56">
          <p className="text-2xl font-serif text-gold mb-8 leading-relaxed" data-id="element-57">
            You know that feeling.
          </p>
          <p className="text-cream/80 leading-relaxed mb-6" data-id="element-58">
            You sit down. You describe exactly what you want. The barber nods —
            halfway listening, halfway watching the TV on the wall. Twenty
            minutes later, you're staring at yourself in the mirror...
          </p>
          <p className="text-cream/80 leading-relaxed mb-6" data-id="element-59">And it's fine.</p>
          <p className="text-cream/80 leading-relaxed mb-6" data-id="element-60">
            Not great. Not what you imagined. Just... fine.
          </p>
          <p className="text-cream/80 leading-relaxed mb-6" data-id="element-61">
            You pay. You tip. You walk out. And somewhere in the back of your
            mind, you're already thinking: "Maybe I'll try somewhere else next
            time."
          </p>
          <p className="text-xl font-serif text-gold my-8" data-id="element-62">
            That's not how it should be.
          </p>
          <p className="text-cream/80 leading-relaxed mb-6" data-id="element-63">
            Because a great haircut isn't just about hair. It's about how you
            walk into a room. How people look at you when you speak. How you
            feel standing in front of the mirror on a Monday morning before the
            week tries to take you down.
          </p>
          <p className="text-cream/80 leading-relaxed font-medium" data-id="element-64">
            Your image is one of the first things the world judges you on. You
            deserve a barber who takes that as seriously as you do.
          </p>
        </div>
      </FadeIn>
    </div>
  </section>;
const Agitation = () => <section className="py-24 bg-darker border-y border-gold/10" data-id="element-65">
    <div className="max-w-3xl mx-auto px-6" data-id="element-66">
      <FadeIn data-id="element-67">
        <h2 className="text-3xl md:text-4xl font-serif text-gold mb-10 text-center" data-id="element-68">
          The painful truth most men never talk about:
        </h2>
        <p className="text-2xl text-center text-cream mb-12 font-serif italic" data-id="element-69">
          The barbershop industry is broken.
        </p>

        <div className="space-y-8 text-cream/80 text-lg leading-relaxed" data-id="element-70">
          <p data-id="element-71">
            You've experienced it. The walk-in shop where you wait 45 minutes
            only to feel like you're on a conveyor belt. The barber who's
            clearly rushing to get to the next client. The inconsistent results
            — amazing cut one week, disappointing the next — depending on who's
            available.
          </p>
          <p data-id="element-72">
            You've tried explaining your fade with photos. You've tried being
            more specific. You've tried different shops across Gwinnett County.
          </p>
          <p data-id="element-73">
            And yet — most of the time — you leave feeling like your time wasn't
            valued. Your vision wasn't heard. Your standards weren't met.
          </p>
          <div className="p-8 border border-gold/20 bg-dark mt-12 text-center" data-id="element-74">
            <p className="text-xl font-serif text-gold" data-id="element-75">
              The problem isn't you. The problem is a system built for volume,
              not for you.
            </p>
          </div>
        </div>
      </FadeIn>
    </div>
  </section>;
const Dream = () => <section className="py-24 bg-dark" data-id="element-76">
    <div className="max-w-3xl mx-auto px-6" data-id="element-77">
      <FadeIn data-id="element-78">
        <h2 className="text-3xl font-serif text-gold mb-8" data-id="element-79">
          Now close your eyes for a second.
        </h2>
        <div className="space-y-6 text-cream/80 text-lg leading-relaxed" data-id="element-80">
          <p data-id="element-81">
            Imagine walking into a clean, professional space where you're
            expected. Where someone greets you by name. Where you sit down and
            the barber doesn't just ask "what are you getting?" — he listens. He
            studies your face, your hair texture, your personal style.
          </p>
          <p data-id="element-82">
            He works with precision and intention. Every line clean. Every fade
            seamless. Every detail sharp.
          </p>
          <p data-id="element-83">
            And when he's done — you look in that mirror and you feel it
            immediately.
          </p>
          <p className="text-2xl font-serif text-gold my-8 italic" data-id="element-84">
            That feeling. That "I own the room" feeling.
          </p>
          <p data-id="element-85">
            That's not a fantasy. That's what a haircut is supposed to feel
            like. That's what you've been chasing every single time you walked
            into a new shop hoping this one would be different.
          </p>
          <p className="text-xl font-medium text-cream mt-8" data-id="element-86">
            That's what Cristian Barber delivers. Every single visit.
          </p>
        </div>
      </FadeIn>
    </div>
  </section>;
const WhyOldSolutionsFail = () => <section id="why-us" className="py-24 bg-darker" data-id="element-87">
    <div className="max-w-4xl mx-auto px-6" data-id="element-88">
      <FadeIn data-id="element-89">
        <h2 className="text-3xl md:text-4xl font-serif text-gold mb-12 text-center" data-id="element-90">
          Why Old Solutions Fail
        </h2>
        <p className="text-xl text-center text-cream/80 mb-16" data-id="element-91">
          You've probably already tried the obvious options.
        </p>

        <div className="grid md:grid-cols-3 gap-8 mb-16" data-id="element-92">
          <div className="p-8 border border-gold/10 bg-dark hover:border-gold/30 transition-colors" data-id="element-93">
            <h3 className="text-xl font-serif text-gold mb-4" data-id="element-94">
              The Walk-in Chains
            </h3>
            <p className="text-cream/70 leading-relaxed" data-id="element-95">
              They're designed for speed, not craft. You're in and out before
              the barber even gets a feel for your style.
            </p>
          </div>
          <div className="p-8 border border-gold/10 bg-dark hover:border-gold/30 transition-colors" data-id="element-96">
            <h3 className="text-xl font-serif text-gold mb-4" data-id="element-97">
              Google Roulette
            </h3>
            <p className="text-cream/70 leading-relaxed" data-id="element-98">
              Reviews can't predict consistency. What you get depends entirely
              on who picks up the clippers that day.
            </p>
          </div>
          <div className="p-8 border border-gold/10 bg-dark hover:border-gold/30 transition-colors" data-id="element-99">
            <h3 className="text-xl font-serif text-gold mb-4" data-id="element-100">
              The Neighborhood Shop
            </h3>
            <p className="text-cream/70 leading-relaxed" data-id="element-101">
              Great energy — but if it's packed, you're waiting, and a rushed
              barber is never your friend.
            </p>
          </div>
        </div>

        <div className="text-center max-w-2xl mx-auto" data-id="element-102">
          <p className="text-lg text-cream/80 leading-relaxed mb-6" data-id="element-103">
            The real issue is structural: most barbershops split their attention
            between too many clients, too many distractions, and too little care
            for the individual.
          </p>
          <p className="text-xl font-serif text-gold" data-id="element-104">
            You've been tolerating a system that was never designed to put you
            first.
          </p>
        </div>
      </FadeIn>
    </div>
  </section>;
const Mechanism = () => <section className="py-24 bg-dark" data-id="element-105">
    <div className="max-w-3xl mx-auto px-6" data-id="element-106">
      <FadeIn data-id="element-107">
        <div className="text-center mb-16" data-id="element-108">
          <h2 className="text-sm tracking-widest uppercase text-gold mb-4" data-id="element-109">
            The Mechanism
          </h2>
          <h3 className="text-4xl font-serif text-cream" data-id="element-110">
            The "Dedicated Chair" Experience™
          </h3>
        </div>

        <div className="space-y-8 text-cream/80 text-lg leading-relaxed" data-id="element-111">
          <p data-id="element-112">Here's what makes Cristian Barber fundamentally different:</p>
          <p className="text-2xl font-serif text-gold py-4 border-y border-gold/20 text-center" data-id="element-113">
            We are appointment-only. Period.
          </p>
          <p data-id="element-114">That's not just a scheduling preference — it's a philosophy.</p>
          <p data-id="element-115">
            When you book your appointment at Cristian Barber, that time is
            yours. There's no one rushing us. No waiting room anxiety. No barber
            mentally half-finished with you while thinking about the next client
            in line.
          </p>
          <p data-id="element-116">
            It's just you, the chair, and a professional who has dedicated his
            craft to one thing: making you look and feel exceptional.
          </p>
          <p data-id="element-117">
            Cristian brings a precision-first approach to every cut — studying
            your fade lines, your facial structure, your style goals — and
            executing with the kind of clean detail that you notice the moment
            you look in the mirror.
          </p>
          <p className="text-xl font-medium text-cream mt-8 text-center" data-id="element-118">
            This isn't a haircut. It's a grooming experience built around you.
          </p>
        </div>
      </FadeIn>
    </div>
  </section>;
const Introducing = () => <section className="py-24 bg-darker border-t border-gold/10" data-id="element-119">
    <div className="max-w-4xl mx-auto px-6 text-center" data-id="element-120">
      <FadeIn data-id="element-121">
        <h2 className="text-4xl font-serif text-gold mb-8" data-id="element-122">
          Introducing Cristian Barber
        </h2>
        <div className="inline-flex items-center gap-3 px-6 py-3 border border-gold/20 bg-dark mb-8" data-id="element-123">
          <MapPin className="text-gold" size={20} data-id="element-124" />
          <span className="text-cream tracking-wide" data-id="element-125">
            2070 Sugarloaf Pkwy #300, Lawrenceville, GA
          </span>
        </div>
        <p className="text-xl text-cream/80 leading-relaxed max-w-2xl mx-auto" data-id="element-126">
          Cristian Barber is the appointment-only grooming studio built for men
          who refuse to settle.
        </p>
        <p className="text-lg text-gold mt-8 font-serif italic" data-id="element-127">
          Every client who walks through our door receives:
        </p>
      </FadeIn>
    </div>
  </section>;
const Gallery = () => {
  const cuts = [{
    src: 'https://cdn.magicpatterns.com/uploads/cHZFsxDbXN1K43xHTacbrj/b1.png',
    label: 'Signature Design'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/8zwLEs5Vrkvkxygt9QiiMX/b2.png',
    label: 'Classic Pompadour'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/oASX3fhkTfB9qYFmJdZoGj/b3.png',
    label: 'Modern Side Part'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/orKBsne1YtZun27E8kJ7BK/b10.png',
    label: 'Waves & Sharp Lines'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/chJYSP3JGRsQpTHCtV397P/b12.png',
    label: 'Custom Hair Art'
  }];
  return <section id="craft" className="py-24 bg-dark relative overflow-hidden" data-id="element-128">
      <div className="max-w-7xl mx-auto px-6" data-id="element-129">
        <FadeIn data-id="element-130">
          <div className="text-center mb-16" data-id="element-131">
            <div className="flex items-center justify-center gap-3 mb-6 text-gold" data-id="element-132">
              <div className="h-px w-12 bg-gold/60" data-id="element-133"></div>
              <span className="text-xs tracking-[0.3em] uppercase" data-id="element-134">
                The Portfolio
              </span>
              <div className="h-px w-12 bg-gold/60" data-id="element-135"></div>
            </div>
            <h2 className="text-4xl md:text-5xl font-serif text-gold mb-6" data-id="element-136">
              The Craft, In Detail
            </h2>
            <p className="text-lg text-cream/70 max-w-2xl mx-auto leading-relaxed" data-id="element-137">
              Precision fades. Custom designs. Clean lines. Every cut speaks for
              itself.
            </p>
          </div>
        </FadeIn>

        {/* Editorial asymmetric gallery */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 md:gap-6" data-id="element-138">
          {/* Feature - large */}
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} className="md:col-span-4 md:row-span-2 group relative overflow-hidden border border-gold/20 aspect-[4/5] md:aspect-auto" data-id="element-139">
            <img src={cuts[0].src} alt={cuts[0].label} className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105" data-id="element-140" />
            <div className="absolute inset-0 bg-gradient-to-t from-dark/90 via-dark/20 to-transparent opacity-80 group-hover:opacity-100 transition-opacity duration-500" data-id="element-141"></div>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-500" data-id="element-142">
              <div className="flex items-center gap-2 text-gold text-xs tracking-[0.3em] uppercase mb-2" data-id="element-143">
                <Scissors size={12} data-id="element-144" />
                Featured Work
              </div>
              <p className="text-2xl md:text-3xl font-serif text-cream" data-id="element-145">
                {cuts[0].label}
              </p>
            </div>
            <div className="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-gold opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-146"></div>
            <div className="absolute bottom-4 left-4 w-8 h-8 border-b-2 border-l-2 border-gold opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-147"></div>
          </motion.div>

          {/* Side images */}
          {cuts.slice(1).map((cut, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.7,
          delay: 0.15 + idx * 0.1,
          ease: 'easeOut'
        }} className="md:col-span-2 group relative overflow-hidden border border-gold/20 aspect-square" data-id="element-148">
              <img src={cut.src} alt={cut.label} className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110" data-id="element-149" />
              <div className="absolute inset-0 bg-gradient-to-t from-dark/90 via-dark/10 to-transparent opacity-70 group-hover:opacity-100 transition-opacity duration-500" data-id="element-150"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5 transform translate-y-1 group-hover:translate-y-0 transition-transform duration-500" data-id="element-151">
                <p className="text-sm md:text-base font-serif text-cream group-hover:text-gold transition-colors duration-300" data-id="element-152">
                  {cut.label}
                </p>
              </div>
              <div className="absolute inset-0 ring-1 ring-inset ring-gold/0 group-hover:ring-gold/40 transition-all duration-500 pointer-events-none" data-id="element-153"></div>
            </motion.div>)}
        </div>

        <FadeIn delay={0.3} data-id="element-154">
          <div className="text-center mt-16" data-id="element-155">
            <p className="text-cream/60 italic font-serif text-lg max-w-2xl mx-auto mb-8" data-id="element-156">
              "The chair doesn't lie. Neither does the mirror."
            </p>
            <a href="#book" className="inline-flex items-center gap-3 border border-gold text-gold px-8 py-4 font-medium tracking-widest uppercase text-sm hover:bg-gold hover:text-dark transition-colors" data-id="element-157">
              Book Your Cut
              <ChevronRight size={16} data-id="element-158" />
            </a>
          </div>
        </FadeIn>
      </div>
    </section>;
};
const Benefits = () => {
  const benefits = [{
    deliver: 'Precision Fades & Clean Lines',
    means: 'You walk out looking sharp, polished, and put-together — every time'
  }, {
    deliver: 'Appointment-Only Service',
    means: 'Your time is respected. Zero waiting. Zero wasted hours'
  }, {
    deliver: 'Personalized Consultation',
    means: 'Your style, your face, your fade — not a generic template'
  }, {
    deliver: 'Modern Looks & Techniques',
    means: 'Stay current. Stay fresh. Stay ahead.'
  }, {
    deliver: 'Professional Environment',
    means: 'Comfortable, clean, focused — a space that matches your standards'
  }, {
    deliver: 'Dedicated 1-on-1 Attention',
    means: 'Every detail executed without rush or distraction'
  }];
  return <section className="py-12 bg-darker pb-24" data-id="element-159">
      <div className="max-w-5xl mx-auto px-6" data-id="element-160">
        <FadeIn data-id="element-161">
          <div className="grid md:grid-cols-2 gap-px bg-gold/20 border border-gold/20" data-id="element-162">
            <div className="hidden md:block bg-dark p-6 text-gold font-serif text-xl border-b border-gold/20" data-id="element-163">
              What We Deliver
            </div>
            <div className="hidden md:block bg-dark p-6 text-gold font-serif text-xl border-b border-gold/20" data-id="element-164">
              What It Means For You
            </div>

            {benefits.map((benefit, idx) => <Fragment key={idx} data-id="element-165">
                <div className="bg-dark p-6 flex items-center gap-4" data-id="element-166">
                  <Star className="text-gold shrink-0" size={16} data-id="element-167" />
                  <span className="text-cream font-medium" data-id="element-168">
                    {benefit.deliver}
                  </span>
                </div>
                <div className="bg-dark p-6 text-cream/70 flex items-center border-b md:border-b-0 border-gold/10" data-id="element-169">
                  {benefit.means}
                </div>
              </Fragment>)}
          </div>
        </FadeIn>
      </div>
    </section>;
};
const Testimonials = () => <section id="testimonials" className="py-24 bg-dark border-y border-gold/10" data-id="element-170">
    <div className="max-w-4xl mx-auto px-6" data-id="element-171">
      <FadeIn data-id="element-172">
        <div className="grid md:grid-cols-2 gap-12 mb-16" data-id="element-173">
          <div className="relative p-8 border border-gold/20 bg-darker" data-id="element-174">
            <Quote className="absolute top-6 left-6 text-gold/20" size={48} data-id="element-175" />
            <p className="relative z-10 text-lg text-cream/90 leading-relaxed mb-6 pt-4" data-id="element-176">
              "I've been to a lot of barbershops in Gwinnett County. Cristian is
              on another level. The attention to detail, the clean fade — I
              leave feeling like a new man every single time."
            </p>
            <p className="text-gold font-serif" data-id="element-177">
              — Satisfied Client, Lawrenceville
            </p>
          </div>
          <div className="relative p-8 border border-gold/20 bg-darker" data-id="element-178">
            <Quote className="absolute top-6 left-6 text-gold/20" size={48} data-id="element-179" />
            <p className="relative z-10 text-lg text-cream/90 leading-relaxed mb-6 pt-4" data-id="element-180">
              "Finally found my barber. I don't even bother looking anywhere
              else. The appointment system alone is worth it — no waiting, just
              results."
            </p>
            <p className="text-gold font-serif" data-id="element-181">— Regular Client</p>
          </div>
        </div>
        <div className="text-center" data-id="element-182">
          <p className="text-xl font-serif text-gold italic" data-id="element-183">
            The proof is in every clean line. Every sharp detail. Every man who
            books once — and never goes anywhere else.
          </p>
        </div>
      </FadeIn>
    </div>
  </section>;
const Offer = () => {
  const items = ['A dedicated, personalized consultation', 'A precision cut tailored to your face and style', 'Expert fade execution — clean, seamless, sharp', 'Detailed finishing touches — lines, edges, everything', 'A professional environment designed for your comfort', 'The confidence that walks out with you'];
  return <section className="py-24 bg-darker" data-id="element-184">
      <div className="max-w-3xl mx-auto px-6" data-id="element-185">
        <FadeIn data-id="element-186">
          <h2 className="text-3xl font-serif text-gold mb-12 text-center" data-id="element-187">
            When you book at Cristian Barber, here's what you receive:
          </h2>
          <ul className="space-y-6 mb-16" data-id="element-188">
            {items.map((item, idx) => <li key={idx} className="flex items-start gap-4 text-lg text-cream/90" data-id="element-189">
                <Check className="text-gold shrink-0 mt-1" size={20} data-id="element-190" />
                <span data-id="element-191">{item}</span>
              </li>)}
          </ul>
          <div className="text-center p-8 border border-gold/20 bg-dark" data-id="element-192">
            <p className="text-xl font-serif text-gold" data-id="element-193">
              This is not a volume shop. This is your personal grooming standard
              — elevated.
            </p>
          </div>
        </FadeIn>
      </div>
    </section>;
};
const RiskReversal = () => <section className="py-24 bg-dark" data-id="element-194">
    <div className="max-w-3xl mx-auto px-6 text-center" data-id="element-195">
      <FadeIn data-id="element-196">
        <h2 className="text-3xl font-serif text-gold mb-8" data-id="element-197">
          Our Commitment To You
        </h2>
        <div className="space-y-6 text-cream/80 text-lg leading-relaxed" data-id="element-198">
          <p data-id="element-199">We know what it feels like to take a chance on a new barber.</p>
          <p data-id="element-200">
            That's exactly why we operate on appointment-only. Because when you
            book with us, we're making a commitment to you — not just filling a
            slot on a schedule.
          </p>
          <p data-id="element-201">
            Your satisfaction, your image, your confidence — that's what we're
            accountable to.
          </p>
          <p className="text-2xl font-serif text-gold mt-8 italic" data-id="element-202">
            If you're not walking out feeling sharper and more confident than
            when you walked in, we haven't done our job.
          </p>
        </div>
      </FadeIn>
    </div>
  </section>;
const Urgency = () => <section className="py-16 bg-darker border-y border-gold/10" data-id="element-203">
    <div className="max-w-3xl mx-auto px-6 text-center" data-id="element-204">
      <FadeIn data-id="element-205">
        <p className="text-xl text-cream/90 mb-6" data-id="element-206">
          Because we're appointment-only, our schedule fills up fast.
        </p>
        <p className="text-lg text-cream/70 mb-10" data-id="element-207">
          We don't overbook. We don't rush. Which means the spots we have are
          genuinely limited — and they go to the men who decide they're done
          settling.
        </p>

        <div className="inline-flex items-center gap-4 bg-gold/10 border border-gold/30 px-8 py-6 rounded-sm mb-10" data-id="element-208">
          <AlertCircle className="text-gold shrink-0" size={24} data-id="element-209" />
          <p className="text-gold font-medium tracking-wide" data-id="element-210">
            We're open from 9:00 AM and appointments are claimed quickly.
          </p>
        </div>

        <p className="text-xl font-serif text-cream italic" data-id="element-211">
          The longer you wait to book, the longer you spend tolerating "good
          enough."
        </p>
      </FadeIn>
    </div>
  </section>;
const CTASection = () => <section id="book" className="py-24 bg-dark relative overflow-hidden" data-id="element-212">
    <div className="absolute inset-0 opacity-5" data-id="element-213">
      <img src={LOGO_URL} alt="" className="w-full h-full object-cover" data-id="element-214" />
    </div>
    <div className="max-w-4xl mx-auto px-6 relative z-10 text-center" data-id="element-215">
      <FadeIn data-id="element-216">
        <h2 className="text-4xl md:text-5xl font-serif text-gold mb-12 leading-tight" data-id="element-217">
          Ready to Experience What a Great Haircut Actually Feels Like?
        </h2>
        <p className="text-2xl text-cream mb-12" data-id="element-218">
          Book Your Appointment at Cristian Barber Today
        </p>

        <div className="flex flex-col items-center gap-6 mb-16 text-cream/80" data-id="element-219">
          <div className="flex items-center gap-3" data-id="element-220">
            <MapPin className="text-gold" size={20} data-id="element-221" />
            <span data-id="element-222">2070 Sugarloaf Pkwy #300, Lawrenceville, GA 30045</span>
          </div>
          <div className="flex items-center gap-3" data-id="element-223">
            <Phone className="text-gold" size={20} data-id="element-224" />
            <span data-id="element-225">Call or Text: (470) 403-6260</span>
          </div>
          <div className="flex items-center gap-3" data-id="element-226">
            <Clock className="text-gold" size={20} data-id="element-227" />
            <span data-id="element-228">
              We're open from 9:00 AM (additional hours — call to confirm)
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-4 max-w-xl mx-auto" data-id="element-229">
          <a href="tel:+14704036260" className="w-full flex items-center justify-center gap-3 bg-gold text-dark px-8 py-5 font-medium tracking-widest uppercase hover:bg-gold-light transition-colors text-lg" data-id="element-230">
            <Phone size={20} data-id="element-231" />
            Call Now To Book Your Spot
          </a>
          <a href="sms:+14704036260" className="w-full flex items-center justify-center gap-3 border border-gold text-gold px-8 py-5 font-medium tracking-widest uppercase hover:bg-gold/10 transition-colors text-lg" data-id="element-232">
            <MessageSquare size={20} data-id="element-233" />
            Text Us Your Preferred Time
          </a>
        </div>

        <p className="mt-12 text-xl font-serif text-gold italic" data-id="element-234">
          Walk in looking like you. Walk out looking like the best version of
          you.
        </p>
        <div className="mt-8 text-cream/70 uppercase tracking-widest text-sm space-y-2" data-id="element-235">
          <p data-id="element-236">Because you don't just need a haircut.</p>
          <p className="text-gold font-medium" data-id="element-237">You need the right barber.</p>
        </div>
      </FadeIn>
    </div>
  </section>;
const LocationMap = () => {
  const mapSrc = 'https://www.google.com/maps?q=2070+Sugarloaf+Pkwy+%23300,+Lawrenceville,+GA+30045&output=embed';
  return <section id="visit" className="py-24 bg-dark relative overflow-hidden" data-id="element-238">
      <div className="max-w-6xl mx-auto px-6" data-id="element-239">
        <FadeIn data-id="element-240">
          <div className="text-center mb-16" data-id="element-241">
            <div className="flex items-center justify-center gap-3 mb-6 text-gold" data-id="element-242">
              <div className="h-px w-12 bg-gold/60" data-id="element-243"></div>
              <span className="text-xs tracking-[0.3em] uppercase" data-id="element-244">
                Find Us
              </span>
              <div className="h-px w-12 bg-gold/60" data-id="element-245"></div>
            </div>
            <h2 className="text-4xl md:text-5xl font-serif text-gold mb-4" data-id="element-246">
              Visit The Studio
            </h2>
            <p className="text-lg text-cream/70 max-w-2xl mx-auto leading-relaxed" data-id="element-247">
              Located in the heart of Lawrenceville — easy to find, impossible
              to forget.
            </p>
          </div>
        </FadeIn>

        <motion.div initial={{
        opacity: 0,
        scale: 0.96
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 1,
        ease: 'easeOut'
      }} className="relative holo-glow border border-gold/40 bg-dark" data-id="element-248">
          {/* The Map */}
          <div className="relative aspect-[16/10] md:aspect-[21/9] overflow-hidden" data-id="element-249">
            <iframe src={mapSrc} width="100%" height="100%" style={{
            border: 0,
            filter: 'grayscale(0.7) contrast(1.1) sepia(0.4) hue-rotate(355deg) saturate(1.4) brightness(0.85)'
          }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Cristian Barber Location" className="absolute inset-0 w-full h-full" data-id="element-250"></iframe>

            {/* Holographic gold tint overlay */}
            <div className="absolute inset-0 bg-gold/5 mix-blend-overlay pointer-events-none" data-id="element-251"></div>

            {/* Scan lines */}
            <div className="absolute inset-0 holo-scanlines pointer-events-none" data-id="element-252"></div>

            {/* Moving scan bar */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-253">
              <div className="holo-scan-bar absolute inset-x-0 h-32" data-id="element-254"></div>
            </div>

            {/* Flickering grid overlay */}
            <div className="absolute inset-0 pointer-events-none holo-flicker" style={{
            backgroundImage: 'linear-gradient(rgba(212,175,55,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.15) 1px, transparent 1px)',
            backgroundSize: '40px 40px'
          }} data-id="element-255"></div>

            {/* Corner brackets */}
            <div className="absolute top-4 left-4 w-12 h-12 border-t-2 border-l-2 border-gold pointer-events-none" data-id="element-256"></div>
            <div className="absolute top-4 right-4 w-12 h-12 border-t-2 border-r-2 border-gold pointer-events-none" data-id="element-257"></div>
            <div className="absolute bottom-4 left-4 w-12 h-12 border-b-2 border-l-2 border-gold pointer-events-none" data-id="element-258"></div>
            <div className="absolute bottom-4 right-4 w-12 h-12 border-b-2 border-r-2 border-gold pointer-events-none" data-id="element-259"></div>

            {/* HUD info badge */}
            <div className="absolute top-6 left-1/2 -translate-x-1/2 pointer-events-none" data-id="element-260">
              <div className="bg-dark/80 backdrop-blur-sm border border-gold/40 px-4 py-2 flex items-center gap-2" data-id="element-261">
                <div className="w-2 h-2 bg-gold rounded-full animate-pulse" data-id="element-262"></div>
                <span className="text-gold text-[10px] tracking-[0.3em] uppercase font-mono" data-id="element-263">
                  Studio Location · Live
                </span>
              </div>
            </div>

            {/* Coordinates HUD */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 pointer-events-none" data-id="element-264">
              <div className="bg-dark/80 backdrop-blur-sm border border-gold/30 px-4 py-2" data-id="element-265">
                <span className="text-gold/80 text-[10px] tracking-[0.25em] font-mono" data-id="element-266">
                  33.9526° N · 84.0027° W
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        <FadeIn delay={0.2} data-id="element-267">
          <div className="mt-12 grid md:grid-cols-3 gap-6 text-center" data-id="element-268">
            <div className="p-6 border border-gold/10 bg-darker" data-id="element-269">
              <MapPin className="text-gold mx-auto mb-3" size={22} data-id="element-270" />
              <p className="text-xs tracking-[0.25em] uppercase text-cream/50 mb-2" data-id="element-271">
                Address
              </p>
              <p className="text-cream/90 text-sm leading-relaxed" data-id="element-272">
                2070 Sugarloaf Pkwy #300
                <br data-id="element-273" />
                Lawrenceville, GA 30045
              </p>
            </div>
            <div className="p-6 border border-gold/10 bg-darker" data-id="element-274">
              <Phone className="text-gold mx-auto mb-3" size={22} data-id="element-275" />
              <p className="text-xs tracking-[0.25em] uppercase text-cream/50 mb-2" data-id="element-276">
                Call or Text
              </p>
              <a href="tel:+14704036260" className="text-cream/90 text-sm hover:text-gold transition-colors" data-id="element-277">
                (470) 403-6260
              </a>
            </div>
            <div className="p-6 border border-gold/10 bg-darker" data-id="element-278">
              <Clock className="text-gold mx-auto mb-3" size={22} data-id="element-279" />
              <p className="text-xs tracking-[0.25em] uppercase text-cream/50 mb-2" data-id="element-280">
                Hours
              </p>
              <p className="text-cream/90 text-sm" data-id="element-281">
                Open from 9:00 AM
                <br data-id="element-282" />
                <span className="text-cream/50 text-xs" data-id="element-283">Call to confirm</span>
              </p>
            </div>
          </div>

          <div className="text-center mt-10" data-id="element-284">
            <a href="https://www.google.com/maps/dir/?api=1&destination=2070+Sugarloaf+Pkwy+%23300,+Lawrenceville,+GA+30045" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 border border-gold text-gold px-8 py-4 font-medium tracking-widest uppercase text-sm hover:bg-gold hover:text-dark transition-colors" data-id="element-285">
              <MapPin size={16} data-id="element-286" />
              Get Directions
            </a>
          </div>
        </FadeIn>
      </div>
    </section>;
};
const OneLiners = () => {
  const lines = ["A bad haircut doesn't just affect how you look — it affects how you show up.", "You've been paying for mediocre. You deserve precision.", "The right barber doesn't just see your hair — he sees your standard.", 'Stop describing what you want. Start experiencing what you deserve.', 'Your appearance is your first impression. Make it impossible to ignore.', 'Volume shops are built for them. Cristian Barber was built for you.', "Clean fades don't happen by accident. They happen by appointment.", "You're one haircut away from walking different.", "The chair doesn't lie. Neither does the mirror.", 'Most men find a great barber by accident. You found yours on purpose.'];
  return <section className="py-24 bg-darker border-y border-gold/10 overflow-hidden" data-id="element-287">
      <div className="max-w-7xl mx-auto px-6" data-id="element-288">
        <FadeIn data-id="element-289">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-290">
            {lines.map((line, idx) => <div key={idx} className="p-8 border border-gold/10 bg-dark hover:border-gold/30 transition-colors flex items-center" data-id="element-291">
                <p className="text-lg font-serif text-cream/90 leading-relaxed" data-id="element-292">
                  "{line}"
                </p>
              </div>)}
          </div>
        </FadeIn>
      </div>
    </section>;
};
const FAQ = () => {
  const faqs = [{
    q: 'Do I need to book in advance?',
    a: "Yes — and that's the point. Your appointment means dedicated, unrushed, personalized service from the moment you sit down."
  }, {
    q: "What if I don't know exactly what I want?",
    a: "Perfect. That's what the consultation is for. Cristian will guide you to the cut that fits your face, your style, and your life."
  }, {
    q: 'Is it worth it compared to a regular shop?',
    a: 'Ask yourself: how much is your time worth? Your image worth? Your confidence worth? One visit to Cristian Barber answers that question.'
  }, {
    q: 'Where are you located?',
    a: "2070 Sugarloaf Pkwy #300, Lawrenceville, GA 30045 — easy to find, professional environment, exactly what you'd expect."
  }, {
    q: 'How do I book?',
    a: "Simple. Call or text (470) 403-6260 and we'll lock in your time."
  }];
  const [openIdx, setOpenIdx] = useState<number | null>(0);
  return <section id="faq" className="py-24 bg-dark" data-id="element-293">
      <div className="max-w-3xl mx-auto px-6" data-id="element-294">
        <FadeIn data-id="element-295">
          <h2 className="text-3xl font-serif text-gold mb-12 text-center" data-id="element-296">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4" data-id="element-297">
            {faqs.map((faq, idx) => <div key={idx} className="border border-gold/20 bg-darker" data-id="element-298">
                <button className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" onClick={() => setOpenIdx(openIdx === idx ? null : idx)} data-id="element-299">
                  <span className="font-serif text-lg text-cream" data-id="element-300">{faq.q}</span>
                  <ChevronDown className={`text-gold transition-transform duration-300 ${openIdx === idx ? 'rotate-180' : ''}`} size={20} data-id="element-301" />
                </button>
                {openIdx === idx && <div className="px-6 pb-5 text-cream/70 leading-relaxed" data-id="element-302">
                    {faq.a}
                  </div>}
              </div>)}
          </div>
        </FadeIn>
      </div>
    </section>;
};
const LegalModal = ({
  open,
  onClose,
  type
}: {
  open: boolean;
  onClose: () => void;
  type: 'privacy' | 'terms' | null;
}) => {
  if (!type) return null;
  const isPrivacy = type === 'privacy';
  return <AnimatePresence data-id="element-303">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.25
    }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8" data-id="element-304">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} data-id="element-305"></div>
          <motion.div initial={{
        opacity: 0,
        y: 30,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.96
      }} transition={{
        duration: 0.35,
        ease: 'easeOut'
      }} className="relative bg-darker border border-gold/30 max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" data-id="element-306">
            <div className="flex items-center justify-between px-6 md:px-8 py-5 border-b border-gold/20" data-id="element-307">
              <div className="flex items-center gap-3" data-id="element-308">
                <Scissors className="text-gold" size={18} data-id="element-309" />
                <h3 className="font-serif text-xl md:text-2xl text-gold" data-id="element-310">
                  {isPrivacy ? 'Privacy Policy' : 'Terms of Service'}
                </h3>
              </div>
              <button onClick={onClose} className="text-cream/60 hover:text-gold transition-colors" aria-label="Close" data-id="element-311">
                <X size={22} data-id="element-312" />
              </button>
            </div>

            <div className="overflow-y-auto px-6 md:px-8 py-6 text-cream/75 text-sm leading-relaxed space-y-5" data-id="element-313">
              {isPrivacy ? <>
                  <p className="text-cream/50 text-xs tracking-widest uppercase" data-id="element-314">
                    Last updated:{' '}
                    {new Date().toLocaleDateString('en-US', {
                month: 'long',
                year: 'numeric'
              })}
                  </p>
                  <div data-id="element-315">
                    <h4 className="font-serif text-gold text-base mb-2" data-id="element-316">
                      1. Information We Collect
                    </h4>
                    <p data-id="element-317">
                      When you contact Cristian Barber to book an appointment,
                      we may collect your name, phone number, and preferred
                      appointment time. This information is used solely to
                      schedule and confirm your service.
                    </p>
                  </div>
                  <div data-id="element-318">
                    <h4 className="font-serif text-gold text-base mb-2" data-id="element-319">
                      2. How We Use Your Information
                    </h4>
                    <p data-id="element-320">
                      Your information is used to manage appointments, send
                      confirmations or reminders, and improve the service we
                      provide. We do not sell, rent, or share your personal
                      information with third parties for marketing purposes.
                    </p>
                  </div>
                  <div data-id="element-321">
                    <h4 className="font-serif text-gold text-base mb-2" data-id="element-322">
                      3. Communication
                    </h4>
                    <p data-id="element-323">
                      By calling or texting (470) 403-6260, you consent to
                      receive replies regarding your appointment. Message and
                      data rates may apply. You may opt out at any time by
                      replying STOP.
                    </p>
                  </div>
                  <div data-id="element-324">
                    <h4 className="font-serif text-gold text-base mb-2" data-id="element-325">
                      4. Data Security
                    </h4>
                    <p data-id="element-326">
                      We take reasonable measures to protect your information.
                      However, no method of transmission over the internet or
                      phone is completely secure.
                    </p>
                  </div>
                  <div data-id="element-327">
                    <h4 className="font-serif text-gold text-base mb-2" data-id="element-328">
                      5. Your Rights
                    </h4>
                    <p data-id="element-329">
                      You may request access to, correction of, or deletion of
                      your personal information at any time by contacting us at
                      (470) 403-6260.
                    </p>
                  </div>
                  <div data-id="element-330">
                    <h4 className="font-serif text-gold text-base mb-2" data-id="element-331">
                      6. Contact
                    </h4>
                    <p data-id="element-332">
                      For privacy questions, contact us at 2070 Sugarloaf Pkwy
                      #300, Lawrenceville, GA 30045 · (470) 403-6260.
                    </p>
                  </div>
                </> : <>
                  <p className="text-cream/50 text-xs tracking-widest uppercase" data-id="element-333">
                    Last updated:{' '}
                    {new Date().toLocaleDateString('en-US', {
                month: 'long',
                year: 'numeric'
              })}
                  </p>
                  <div data-id="element-334">
                    <h4 className="font-serif text-gold text-base mb-2" data-id="element-335">
                      1. Appointments
                    </h4>
                    <p data-id="element-336">
                      Cristian Barber operates on an appointment-only basis.
                      Appointments are confirmed by phone or text and must be
                      honored at the scheduled time. We recommend arriving 5
                      minutes before your appointment.
                    </p>
                  </div>
                  <div data-id="element-337">
                    <h4 className="font-serif text-gold text-base mb-2" data-id="element-338">
                      2. Cancellations & No-Shows
                    </h4>
                    <p data-id="element-339">
                      We respectfully ask for at least 24 hours' notice for
                      cancellations or reschedules. Repeated no-shows may result
                      in a deposit being required for future bookings.
                    </p>
                  </div>
                  <div data-id="element-340">
                    <h4 className="font-serif text-gold text-base mb-2" data-id="element-341">
                      3. Late Arrivals
                    </h4>
                    <p data-id="element-342">
                      If you arrive more than 15 minutes late, your appointment
                      may need to be rescheduled to respect the next client's
                      time.
                    </p>
                  </div>
                  <div data-id="element-343">
                    <h4 className="font-serif text-gold text-base mb-2" data-id="element-344">
                      4. Service Quality
                    </h4>
                    <p data-id="element-345">
                      We are committed to delivering exceptional grooming
                      services. If you are not satisfied with your cut, please
                      notify us before leaving the chair so we can make it
                      right.
                    </p>
                  </div>
                  <div data-id="element-346">
                    <h4 className="font-serif text-gold text-base mb-2" data-id="element-347">
                      5. Payment
                    </h4>
                    <p data-id="element-348">
                      Payment is due at the time of service. We accept cash and
                      major credit/debit cards. Gratuity is appreciated but not
                      required.
                    </p>
                  </div>
                  <div data-id="element-349">
                    <h4 className="font-serif text-gold text-base mb-2" data-id="element-350">
                      6. Conduct
                    </h4>
                    <p data-id="element-351">
                      Cristian Barber reserves the right to refuse service to
                      anyone for any lawful reason, including but not limited to
                      disruptive behavior.
                    </p>
                  </div>
                  <div data-id="element-352">
                    <h4 className="font-serif text-gold text-base mb-2" data-id="element-353">
                      7. Contact
                    </h4>
                    <p data-id="element-354">
                      For any questions about these terms, contact us at 2070
                      Sugarloaf Pkwy #300, Lawrenceville, GA 30045 · (470)
                      403-6260.
                    </p>
                  </div>
                </>}
            </div>

            <div className="px-6 md:px-8 py-4 border-t border-gold/20 flex justify-end" data-id="element-355">
              <button onClick={onClose} className="bg-gold text-dark px-6 py-2.5 font-medium tracking-widest uppercase text-xs hover:bg-gold-light transition-colors" data-id="element-356">
                Close
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
};
const Footer = ({
  onOpenLegal
}: {
  onOpenLegal: (type: 'privacy' | 'terms') => void;
}) => <footer className="bg-darker py-16 border-t border-gold/20" data-id="element-357">
    <div className="max-w-6xl mx-auto px-6 flex flex-col items-center text-center" data-id="element-358">
      <img src={LOGO_URL} alt="Cristian Barber" className="h-24 object-contain mb-8 opacity-80" data-id="element-359" />

      <div className="flex flex-col md:flex-row items-center gap-6 md:gap-12 text-cream/60 text-sm tracking-widest uppercase mb-12" data-id="element-360">
        <a href="#experience" className="hover:text-gold transition-colors" data-id="element-361">
          Experience
        </a>
        <a href="#why-us" className="hover:text-gold transition-colors" data-id="element-362">
          Why Us
        </a>
        <a href="#testimonials" className="hover:text-gold transition-colors" data-id="element-363">
          Testimonials
        </a>
        <a href="#faq" className="hover:text-gold transition-colors" data-id="element-364">
          FAQ
        </a>
      </div>

      <div className="space-y-2 text-cream/50 text-sm mb-12" data-id="element-365">
        <p data-id="element-366">2070 Sugarloaf Pkwy #300, Lawrenceville, GA 30045</p>
        <p data-id="element-367">(470) 403-6260</p>
        <p data-id="element-368">Open from 9:00 AM</p>
      </div>

      <div className="flex items-center gap-6 mb-6" data-id="element-369">
        <button onClick={() => onOpenLegal('privacy')} className="text-cream/50 hover:text-gold transition-colors text-xs tracking-widest uppercase" data-id="element-370">
          Privacy Policy
        </button>
        <span className="text-gold/30" data-id="element-371">·</span>
        <button onClick={() => onOpenLegal('terms')} className="text-cream/50 hover:text-gold transition-colors text-xs tracking-widest uppercase" data-id="element-372">
          Terms of Service
        </button>
      </div>

      <p className="text-cream/30 text-xs tracking-widest" data-id="element-373">
        © {new Date().getFullYear()} Cristian Barber. All rights reserved.
      </p>
    </div>
  </footer>;
export function App() {
  const [legalModal, setLegalModal] = useState<'privacy' | 'terms' | null>(null);
  return <div className="min-h-screen bg-dark text-cream font-sans selection:bg-gold selection:text-dark" data-id="element-374">
      <Header data-id="element-375" />
      <main data-id="element-376">
        <Hero data-id="element-377" />
        <Gallery data-id="element-378" />
        <Hook data-id="element-379" />
        <Divider data-id="element-380" />
        <Agitation data-id="element-381" />
        <Divider data-id="element-382" />
        <Dream data-id="element-383" />
        <WhyOldSolutionsFail data-id="element-384" />
        <Divider data-id="element-385" />
        <Mechanism data-id="element-386" />
        <Introducing data-id="element-387" />
        <Benefits data-id="element-388" />
        <Testimonials data-id="element-389" />
        <Offer data-id="element-390" />
        <Divider data-id="element-391" />
        <RiskReversal data-id="element-392" />
        <Urgency data-id="element-393" />
        <LocationMap data-id="element-394" />
        <CTASection data-id="element-395" />
        <OneLiners data-id="element-396" />
        <FAQ data-id="element-397" />
      </main>
      <Footer onOpenLegal={type => setLegalModal(type)} data-id="element-398" />
      <LegalModal open={legalModal !== null} onClose={() => setLegalModal(null)} type={legalModal} data-id="element-399" />
      <BookingToast data-id="element-400" />
    </div>;
}